import { useToast } from '@app/core/notifications';
import { isVideoStation } from '@app/core/stations/helpers';
import { useFragmentsTranscriptionSync } from '@app/modules/fragments/hooks/useFragmentsTranscriptionSync';
import {
  LabelWrapper,
  SwitchWrapper,
} from '@app/modules/logger/screens/stream/components/audit/transcriptions/styles';
import TrascriptionsLabel from '@app/modules/logger/screens/stream/components/audit/transcriptions/TranscriptionsLabel';
import {
  FormattedFragmentsTranscriptionType,
  TranscriptionType,
} from '@app/modules/logger/screens/stream/components/audit/transcriptions/transcriptions.types';
import {
  formatFragmentsTranscript,
  formatSpeakerToSave,
  formatVoiceLabel,
  formatVoiceLabelToUpperCase,
  parseFragmentTranscription,
} from '@app/modules/logger/screens/stream/components/audit/transcriptions/utils';
import { GetFragmentDto, JobStatus, toTimestamp } from '@mi-front/core';
import { MrtTable, NullState, SwitchInput } from '@mi-front/ui';
import { Replay as RestartIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { apiClient } from '../../../../../../api';
import { JobStatusAlert } from '../../../../../audit/components/jobs/JobStatusAlert';

import { usePlaybackRange } from '@app/core/playback/hooks/usePlayback';
import { useStation } from '@app/core/stations/hooks/useStation';
import { WpWordsType } from '@app/modules/search/types';
import { SpeakerEditor } from './editor/SpeakerEditor';
import { useEditor } from './editor/useEditor';
import { WordEditor } from './editor/WordEditor';
import { TranscriptionSkeleton } from './TranscriptionSkeleton';

export function TranscriptionStatus({ fragment }: { fragment: GetFragmentDto }) {
  const { formatMessage } = useIntl();
  const { notifyError } = useToast();
  const { handleUpdateSpeaker, handleUpdateWords } = useEditor();
  const station = useStation(fragment.stationId);
  const isVideo = isVideoStation(station);
  const playbackRange = usePlaybackRange<true>();

  const [visibility, setVisibility] = useState({
    confidence: false,
    editor: false,
    diarization: !isVideo,
    speaker_editor: false,
    'diarization-video': isVideo,
  });

  const query = useQuery({
    queryKey: ['fragment', fragment.id, 'transcription'],
    queryFn: () => apiClient.fragments.transcriptionStatus(fragment.id),
    refetchInterval: (query) => {
      const data = query.state.data;
      const doRefresh =
        !data?.status ||
        [JobStatus.QUEUED, JobStatus.IN_PROGRESS].includes(data?.status as JobStatus);

      return doRefresh ? 10000 : false;
    },
  });
  const { data } = query;

  const parsedData: TranscriptionType[] = useMemo(() => {
    return data?.result && !query.isLoading
      ? parseFragmentTranscription(data?.result as TranscriptionType[][])
      : [];
  }, [query, data?.result]);

  const { fragmentsTranscriptionPlayerSync, timeRef, currentTs } =
    useFragmentsTranscriptionSync(fragment);

  const formattedData = useMemo(() => {
    return parsedData ? formatFragmentsTranscript(parsedData) : [];
  }, [parsedData]);

  if (
    query.isLoading ||
    !data ||
    data.status === JobStatus.IN_PROGRESS ||
    data.status === JobStatus.QUEUED
  ) {
    return (
      <TranscriptionSkeleton
        showMessage={data?.status === JobStatus.IN_PROGRESS || data?.status === JobStatus.QUEUED}
      />
    );
  }

  const handleReplaceAllSpeakers = async (new_voice_label: string, label: string) => {
    const updatedData = formattedData.filter((row) => row.voice_label === label);

    if (updatedData.length > 0) {
      const time = updatedData[0].time;

      const diarization = updatedData.map((row) => ({
        distance: row.distance,
        segment: row.segment,
        label: row.label,
        voice_label: formatSpeakerToSave(new_voice_label),
      }));

      try {
        await handleUpdateSpeaker(fragment.id, time, diarization);
        query.refetch();
      } catch (error) {
        notifyError(
          formatMessage(
            {
              id: 'fragments.transcription.errorUpdateSpeaker',
              defaultMessage: 'Ocurrió un error al intentar actualizar el speaker {speaker}',
            },
            { speaker: new_voice_label },
          ),
        );
      }
    }
  };

  const handleReplaceAllWords = async (new_word: string, old_word: string) => {
    const updatedData = formattedData.filter((row) =>
      row.words?.some((word) => word.word === old_word),
    );

    if (updatedData.length > 0) {
      const time = updatedData[0].time;
      const wpWords = updatedData.reduce((acc, row) => {
        if (row.words) {
          const newWords = row.words.filter((word) => word.word === old_word);
          return [
            ...acc,
            ...newWords.map((word) => ({
              ...word,
              word: new_word,
              confidence: 1,
            })),
          ];
        }

        return acc;
      }, [] as WpWordsType[]);

      try {
        await handleUpdateWords(fragment.id, time, wpWords);
        query.refetch();
      } catch (error) {
        notifyError(
          formatMessage({
            id: 'fragments.transcription.errorUpdateFragment',
            defaultMessage: 'Ocurrió un error al intentar actualizar el fragment',
          }),
        );
      }
    }
  };

  return (
    <>
      <JobStatusAlert fragment={fragment} job={data} />
      {formattedData && formattedData[0] ? (
        <div style={{ marginTop: '1rem' }}>
          <LabelWrapper>
            {visibility.confidence && !visibility.diarization ? <TrascriptionsLabel /> : <div />}
            <SwitchWrapper>
              <SwitchInput
                size={'sm'}
                checked={visibility.confidence}
                onChange={(checked) => {
                  if (visibility.editor) {
                    query.refetch();
                  }

                  setVisibility({
                    confidence: checked,
                    editor: false,
                    diarization: !checked && !isVideo,
                    'diarization-video': !checked && isVideo,
                    speaker_editor: false,
                  });
                }}
                label={formatMessage({
                  id: 'audit.transcriptionScreen.confidence',
                  defaultMessage: 'Eficiencia',
                })}
              />
              <SwitchInput
                size={'sm'}
                checked={visibility.editor}
                onChange={(checked) => {
                  if (!checked) {
                    query.refetch();
                  }

                  setVisibility({
                    confidence: false,
                    editor: checked,
                    diarization: !checked && !isVideo,
                    'diarization-video': !checked && isVideo,
                    speaker_editor: checked,
                  });
                }}
                label={formatMessage({
                  id: 'audit.transcriptionScreen.editor',
                  defaultMessage: 'Editor',
                })}
              />
            </SwitchWrapper>
          </LabelWrapper>
          <MrtTable<FormattedFragmentsTranscriptionType>
            enableSorting={false}
            columns={[
              {
                id: 'info',
                header: formatMessage({
                  id: 'audit.diarization.speaker',
                  defaultMessage: 'Speaker Label',
                }),
                size: 25,
                minSize: 25,
                Cell: ({ row: { original } }) => {
                  return (
                    <span style={{ color: original.color }}>
                      {formatVoiceLabelToUpperCase(formatVoiceLabel(original.voice_label))}
                    </span>
                  );
                },
              },
              {
                id: 'speaker_editor',
                header: formatMessage({
                  id: 'audit.diarization.speakerEditor',
                  defaultMessage: 'Speaker Label',
                }),
                size: 25,
                minSize: 25,
                Cell: ({ row: { original } }) => {
                  return (
                    <SpeakerEditor
                      label={original.label}
                      voice_label={original.voice_label}
                      fragmentId={fragment.id}
                      time={original.time}
                      distance={original.distance}
                      segment={original.segment}
                      onReplaceAll={handleReplaceAllSpeakers}
                    />
                  );
                },
              },
              {
                id: 'confidence',
                size: 200,
                accessorFn: (row) => row.wp_words,
                enableColumnFilter: false,
                header: formatMessage({
                  id: 'audit.transcriptionScreen.sentence',
                  defaultMessage: 'Texto',
                }),
                Cell: ({ row: { index }, renderedCellValue }) => {
                  return <div key={`text-confidence-${index}`}>{renderedCellValue}</div>;
                },
              },
              {
                id: 'diarization-video',
                size: 200,
                accessorFn: (row) => row.words,
                enableColumnFilter: false,
                header: formatMessage({
                  id: 'audit.transcriptionScreen.sentence',
                  defaultMessage: 'Texto',
                }),
                Cell: ({ row: { original } }) => {
                  const [isCurrRow, setIsCurrRow] = useState(false);

                  const segmentStart = toTimestamp(
                    playbackRange.from + original.segment.start * 1000,
                  );

                  const segmentEnd = toTimestamp(playbackRange.from + original.segment.end * 1000);

                  useEffect(() => {
                    setIsCurrRow(segmentStart <= currentTs! && segmentEnd > currentTs!);
                  }, [currentTs]);

                  if (isCurrRow) {
                    timeRef.current = toTimestamp(original.time);
                  }

                  return (
                    <div>
                      {fragmentsTranscriptionPlayerSync(
                        //@ts-ignore
                        original.words,
                        isCurrRow,
                        original.time,
                      )}
                    </div>
                  );
                },
              },
              {
                id: 'diarization',
                size: 200,
                accessorFn: (row) => row.formattedJsx,
                enableColumnFilter: false,
                header: formatMessage({
                  id: 'audit.transcriptionScreen.sentence',
                  defaultMessage: 'Texto',
                }),
                Cell: ({ row: { index }, renderedCellValue }) => (
                  <div key={`text-diarization-${index}`}>{renderedCellValue}</div>
                ),
              },
              {
                id: 'editor',
                size: 200,
                accessorFn: (row) => row.formattedJsx,
                enableColumnFilter: false,
                header: formatMessage({
                  id: 'audit.transcriptionScreen.sentence',
                  defaultMessage: 'Texto',
                }),
                Cell: ({ row: { index, original } }) => {
                  return (
                    <div key={`text-diarization-${index}`}>
                      <WordEditor
                        words={original.words}
                        fragmentId={fragment.id}
                        time={original.time}
                        onReplaceAll={handleReplaceAllWords}
                      />
                    </div>
                  );
                },
              },
            ]}
            data={formattedData}
            memoizeColumns={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableGlobalFilter={false}
            enableHiding={false}
            enablePagination={false}
            enableColumnActions={false}
            enableExpandAll={false}
            enableFullScreenToggle={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            state={{
              columnVisibility: {
                ...visibility,
                speaker_editor: visibility.editor,
                info: !visibility.editor,
              },
              isLoading: query.isLoading,
            }}
            enableStickyHeader
            muiTableContainerProps={{
              sx: {
                maxHeight: '500px', // Use a fixed height or 'calc(100vh - 200px)'
              },
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <NullState className="p-6">
            {station.featuresConfig?.transcription.enabled &&
            station.featuresConfig?.transcription.diarization.fragments ? (
              <FormattedMessage
                id="audit.transcriptionScreen.processingSpeakers"
                defaultMessage="Transcripción finalizada, procesando speakers"
              />
            ) : (
              <FormattedMessage
                id="audit.transcriptionScreen.noResults"
                defaultMessage="No transcriptions found"
              />
            )}
          </NullState>
          <Button
            size="small"
            variant="outlined"
            color="info"
            onClick={() => query.refetch()}
            startIcon={<RestartIcon />}
          >
            <FormattedMessage
              id={'audit.transcriptionScreen.refresh'}
              defaultMessage="Actualizar"
            />
          </Button>
        </div>
      )}
    </>
  );
}
